// Symmetric (AES-256-GCM) and asymmetric (RSA-OAEP) primitives over WebCrypto.
//
// AES-GCM is authenticated: any tampering with the ciphertext, IV, or tag makes
// decrypt throw rather than return corrupt plaintext — so a failed decrypt is
// also how we detect a wrong key (e.g. wrong master password) and tampering.
// A fresh 96-bit IV is generated per encryption; never reuse an IV with a key.

import {
  decodeEnc,
  encodeEnc,
  fromB64,
  fromUtf8,
  randomBytes,
  toB64,
  utf8,
  type Bytes,
} from "./encoding";

const AES = "AES-GCM";
const IV_BYTES = 12;

// The user key is imported extractable so the master password can be rotated
// (re-wrapping the same key). This doesn't weaken the model: any script that
// could export it could already call encrypt/decrypt with it. The stretched key
// and RSA private key, which never need re-exporting, stay non-extractable.
export function importAesKey(raw: Bytes): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", raw, AES, true, ["encrypt", "decrypt"]);
}

export async function aesEncrypt(
  key: CryptoKey,
  plaintext: Bytes,
): Promise<string> {
  const iv = randomBytes(IV_BYTES);
  const ct = await crypto.subtle.encrypt({ name: AES, iv }, key, plaintext);
  return encodeEnc("A1", iv, new Uint8Array(ct));
}

export async function aesDecrypt(key: CryptoKey, enc: string): Promise<Bytes> {
  const { type, iv, ct } = decodeEnc(enc);
  if (type !== "A1" || !iv) throw new Error("expected an A1 (AES-GCM) EncString");
  const pt = await crypto.subtle.decrypt({ name: AES, iv }, key, ct);
  return new Uint8Array(pt);
}

export const aesEncryptString = (key: CryptoKey, s: string): Promise<string> =>
  aesEncrypt(key, utf8(s));

export async function aesDecryptString(
  key: CryptoKey,
  enc: string,
): Promise<string> {
  return fromUtf8(await aesDecrypt(key, enc));
}

// --- RSA-OAEP: used to wrap a vault's symmetric key to a member's public key,
// so shared vaults work without the server ever holding a usable key. ---

const RSA = { name: "RSA-OAEP", hash: "SHA-256" } as const;

export function generateRsaKeyPair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey(
    {
      name: "RSA-OAEP",
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: "SHA-256",
    },
    true,
    ["encrypt", "decrypt"],
  );
}

export async function exportPublicKey(key: CryptoKey): Promise<string> {
  return toB64(new Uint8Array(await crypto.subtle.exportKey("spki", key)));
}

export function importPublicKey(b64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("spki", fromB64(b64), RSA, true, ["encrypt"]);
}

export async function exportPrivateKeyPkcs8(key: CryptoKey): Promise<Bytes> {
  return new Uint8Array(await crypto.subtle.exportKey("pkcs8", key));
}

export function importPrivateKey(pkcs8: Bytes): Promise<CryptoKey> {
  return crypto.subtle.importKey("pkcs8", pkcs8, RSA, false, ["decrypt"]);
}

export async function rsaWrap(
  publicKey: CryptoKey,
  data: Bytes,
): Promise<string> {
  const ct = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, data);
  return encodeEnc("R1", null, new Uint8Array(ct));
}

export async function rsaUnwrap(
  privateKey: CryptoKey,
  enc: string,
): Promise<Bytes> {
  const { type, ct } = decodeEnc(enc);
  if (type !== "R1") throw new Error("expected an R1 (RSA-OAEP) EncString");
  const pt = await crypto.subtle.decrypt({ name: "RSA-OAEP" }, privateKey, ct);
  return new Uint8Array(pt);
}
